import { ContainerYardOperationDialog } from '@host/components/container-change-position-dialog/ContainerYardOperationDialog'
import { ContainerYardOperationViewStore } from '@host/components/container-change-position-dialog/ContainerYardOperationViewStore'
import { useGeneralCargoOrderLocations } from '@host/hooks/useGeneralCargoOrderLocations'
import { parseBaplie } from '@host/utils/baplie-parser-utils'
import { EquipmentPlanningSummary, useOperationsStore } from '@tom-ui/operations'
import { RailVisitDetailsPage } from '@tom-ui/planning'
import { OrderResponseDto } from '@tom-ui/planning/app/api'
import {
  GeneralCargoDamage,
  generalCargoDamageService,
  GeneralCargoOrderReservationDialog,
  OutboundRequestValidationControl,
  useStorageStore,
} from '@tom-ui/storage'
import { PATHS } from '@tom-ui/utils'
import { observer } from 'mobx-react-lite'
import { IssueStatus } from 'modules/operations/src/app/api'
import { OutboundOrderValidationDto } from 'modules/planning/src/stores/gateControl/ValidateOutboundDto'
import { useCallback, useEffect, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router'
import { OrderDamageDto } from '../GeneralCargoOrders/DamageOrderDto'
import { renderIsoCodeMappingInput } from '../IsoCodeMapping/IsoCodeMappingWrapper'

export const RailVisitDetails = observer(() => {
  const { id } = useParams()
  const navigate = useNavigate()

  const railVisitId = Number(id)

  const { equipmentsVesselVisitUIStore, issueStore } = useOperationsStore()
  const { tenantConfigStore, generalCargoOrderReservationUIStore } = useStorageStore()

  useEffect(() => {
    if (railVisitId) equipmentsVesselVisitUIStore.getPlannedEquipments(railVisitId)
  }, [equipmentsVesselVisitUIStore, railVisitId])

  const renderDamage = (orderId: number) => <GeneralCargoDamage orderId={orderId} />

  const validateOutboundRequest = (params: OutboundOrderValidationDto): React.ReactElement => (
    <OutboundRequestValidationControl
      isOutbound={params.isOutbound}
      customerId={params.customerId}
      commodityId={params.commodityId}
      lotNumber={params.lotNumber}
      packageId={params.packageId}
      quantity={params.quantity}
      unitIds={params.unitIds}
      imoClasses={params.imoClasses}
    />
  )

  const containerYardOperationViewStore = useMemo(() => {
    return new ContainerYardOperationViewStore()
  }, [])

  const handleGetOrdersDamageAmount = useCallback(async (orderIds: number[]) => {
    const damages = await generalCargoDamageService.getOrderDamageAmount(orderIds)

    return damages.map(
      (x: any) =>
        ({
          orderId: x.orderId,
          amount: x.totalQuantity,
        }) as OrderDamageDto,
    )
  }, [])

  const handleGeneralCargoReservation = (order: OrderResponseDto) => {
    generalCargoOrderReservationUIStore.toggleDialog(
      order.id,
      undefined,
      order.plannedCargoAmount ?? 0,
      order.direction,
    )
  }

  const { reservationUpdate, handleGetOrderLocations, handleGetOrderReservations } =
    useGeneralCargoOrderLocations()

  return (
    <>
      <RailVisitDetailsPage
        renderDamage={renderDamage}
        renderIsoCodeMappingInput={renderIsoCodeMappingInput}
        getOrderDamagesAmountFunc={handleGetOrdersDamageAmount}
        validateOutboundRequest={validateOutboundRequest}
        equipmentPlanningSummary={
          <EquipmentPlanningSummary
            isForRailVisit
            onViewClick={() =>
              navigate(
                `${PATHS.visits.railVisits.url}/${railVisitId}/${PATHS.visits.railVisits.equipments.url}`,
              )
            }
          />
        }
        unresolvedOperationsIssueCount={
          issueStore.items.filter(
            i =>
              i.carrierVisitIds.includes(railVisitId) && i.issueStatus === IssueStatus.Unresolved,
          ).length
        }
        containerYardOperationViewStore={containerYardOperationViewStore}
        parseBaplie={parseBaplie}
        onOpenGeneralCargoReservation={
          tenantConfigStore.skipYardPlanning ? undefined : handleGeneralCargoReservation
        }
        getOrderLocationsFunc={handleGetOrderLocations}
        getOrderReservationsFunc={handleGetOrderReservations}
        reservationUpdate={reservationUpdate ?? undefined}
      />
      <ContainerYardOperationDialog store={containerYardOperationViewStore} />
      <GeneralCargoOrderReservationDialog />
    </>
  )
})
